import { ModalRef, sendNotification, useTranslation } from '@msdk/common'
import { Entity, GenericFieldValue } from '@msdk/definitions'
import {
  ButtonPrimary,
  ButtonSecondary,
  Card,
  COLORS,
  createComponentWrapperWithRef,
  DialogActions,
  Pagination,
  styled,
  TableWrapper,
  uiTokens,
} from '@msdk/ui'
import React, { useEffect } from 'react'

import { useFormModal, useModalView } from '../../hooks'
import { DynamicView } from '../DynamicView'
import { DynamicViewProvider } from '../DynamicView/context'
import { Filters } from '../Filters'
import { useViewContext } from '../ViewContextProvider'

const SingleViewCardTable = styled(Card)`
  background-color: ${COLORS.components.tab.content.background};
  overflow: auto;
  box-shadow: unset;
  display: flex;
  flex-direction: column;
  flex: 1;
  border: 0;
  gap: ${uiTokens.spacing.padding.medium}px;
`

interface Props {
  entityName: string
  viewName: string
  modalRef?: ModalRef
  lookup: boolean
  multiSelect?: boolean
  selectedRowIds?: string[]
  prefilledFilters?: Record<string, GenericFieldValue>
}

const BodyWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  height: 100%;
  padding: 0 ${uiTokens.spacing.padding.medium}px;
`
const MuiHeader = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 8px 8px 0 0;
  flex-wrap: wrap;
  gap: 8px;

  padding: 16px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: #fff;
`

const NovaHeader = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 8px 8px 0 0;
  flex-wrap: wrap;
  gap: 8px;
`

const Header = createComponentWrapperWithRef(
  {
    mui: MuiHeader,
    nova: NovaHeader,
  },
  'Header',
)

export function SingleModalView(props: Props) {
  const { createModal: createFormModal } = useFormModal()

  const {
    entityModel,
    viewModel,
    isLoading,
    response,
    currentPage,
    acl,
    filterChange,
    onPageChange,
    fetchData,
    onOrderChange,
    reload,
    selectedRows,
    onRowSelected,
    currentFilters,
    isMultiselected,
    setIsMultiselect,
    additionalCondition,
    addColumnFilter,
    removeColumnFilter,
    removeAllColumnFilter,
    isInitialized,
    currentOrder,
    addOrUpdateFilter,
  } = useModalView(props.entityName, props.viewName, !!props.multiSelect, props.selectedRowIds, props.prefilledFilters)

  const viewContext = useViewContext()

  const toggleSelection = (row: Entity) => {
    const newSelection = selectedRows.includes(row) ? selectedRows.filter((r) => r !== row) : selectedRows.concat([row])
    onRowSelected(newSelection)
  }

  const onRowClicked = (row: Entity) => {
    //view mode
    if (!props.lookup) {
      const formToOpen = viewModel.openFormModel ?? entityModel.defaultForm
      if (formToOpen) {
        const modalRef = createFormModal(
          entityModel.name,
          formToOpen,
          row.id,
          () => {
            fetchData()
          },
          { mode: 'Readonly', modalForm: viewModel.modalForm },
        )
        modalRef.open()
      } else {
        sendNotification({
          message:
            'Missing DefaultForm in entity model and no OpenFormModel was specified in the view, can not determine the form to open!',
          status: 'error',
        })
      }
      return
    }

    //lookup mode
    if (isMultiselected) {
      toggleSelection(row)
      return
    }

    onRowSelected([row])
  }

  const onConfirmSelection = (selected: Entity[]) => {
    const dataToReturn = isMultiselected ? selected : selected[0]
    props.modalRef?.close(dataToReturn)
  }

  const onCancel = () => {
    props.modalRef?.close()
  }

  useEffect(() => {
    const newCtx = {
      isLoading,
      response,
      acl,
      reload,
      selectedRows,
      currentFilters,
      fetchData,
      isInitialized,
    }
    viewContext.update(newCtx)
  }, [isLoading, response, acl, reload, selectedRows, currentFilters, fetchData, isInitialized])

  const t = useTranslation()

  return (
    <>
      {viewModel && (
        <BodyWrapper>
          <SingleViewCardTable>
            <Header>
              <Filters
                filters={viewModel.filters}
                viewName={viewModel.name}
                onFilterChange={filterChange}
                prefilledFilters={props.prefilledFilters}
                multiselect={isMultiselected}
                setMultiselect={setIsMultiselect}
                acl={acl}
                entityModel={entityModel}
                hideBinnedItemsFilter={props.lookup}
              />
            </Header>
            <TableWrapper>
              <DynamicViewProvider
                entityModel={entityModel}
                viewModel={viewModel}
                reload={reload}
                rows={response?.source || []}
                isLoading={isLoading}
                onRowClick={onRowClicked}
                onOrderChange={onOrderChange}
                multiSelect={isMultiselected}
                lookup={props.lookup}
                selectedRows={selectedRows}
                onRowSelected={onRowSelected}
                acl={acl}
                modalRef={props.modalRef}
                currentFilters={currentFilters}
                additionalCondition={additionalCondition}
                addColumnFilter={addColumnFilter}
                removeColumnFilter={removeColumnFilter}
                removeAllColumnFilter={removeAllColumnFilter}
                currentOrder={currentOrder}
                addOrUpdateFilter={addOrUpdateFilter}
              >
                <DynamicView />
              </DynamicViewProvider>
            </TableWrapper>
            {!viewModel.hidePagination && (
              <Pagination
                component="div"
                count={response?.totalItems ?? 0}
                page={currentPage}
                rowsPerPage={response?.pageSize ?? 30}
                rowsPerPageOptions={[]}
                onPageChange={(_e, page) => onPageChange(page)}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t('CMPSR_PAGINATOR_OF', 'of')} ${count}`}
              />
            )}
          </SingleViewCardTable>
          {props.lookup && (
            <DialogActions>
              <ButtonSecondary onClick={() => onCancel()}>
                {t('CMPS_VIEW_MULTI_SELECT_CANCEL', 'Cancel')}
              </ButtonSecondary>
              <ButtonPrimary
                onClick={() => onConfirmSelection(selectedRows)}
                disabled={!selectedRows || selectedRows.length === 0}
              >
                {t('CMPS_VIEW_MULTI_SELECT_SAVE', 'Save')}
              </ButtonPrimary>
            </DialogActions>
          )}
        </BodyWrapper>
      )}
    </>
  )
}
